import * as React from 'react'

import {
  Divider,
  Form,
  PrimaryButton,
  Space,
  TertiaryButton,
} from '@procurify/ui'
import { FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'

export interface CreatePODisclaimerLabelFormSchema {
  description: string
  text: string
}

export interface ICreatePODisclaimerLabelFormProps {
  onSubmit: (values: CreatePODisclaimerLabelFormSchema) => void
  onCancel: () => void
  isSaving: boolean
}

export const CreatePODisclaimerLabelForm = ({
  onSubmit,
  onCancel,
  isSaving,
}: ICreatePODisclaimerLabelFormProps) => {
  const intl = useProcurifyIntl()

  const formik = useFormik<CreatePODisclaimerLabelFormSchema>({
    initialValues: { description: '', text: '' },
    validationSchema: Yup.object({
      description: Yup.string().required(
        intl.formatMessage(messages.descriptionRequiredError)
      ),
      text: Yup.string().required(
        intl.formatMessage(messages.contentRequiredError)
      ),
    }),
    onSubmit: (values, { setSubmitting }) => {
      onSubmit(values)
      // The mutation's isSaving is the in-flight indicator; guard failures
      // keep the form open, so Formik must not latch isSubmitting
      setSubmitting(false)
    },
  })

  const handleDescriptionKeyDown = (event: React.KeyboardEvent) => {
    // Enter in the description submits only when content is non-empty
    // (BHV-019)
    if (event.key === 'Enter') {
      event.preventDefault()
      if (formik.values.text.length > 0) {
        formik.handleSubmit()
      }
    }
  }

  return (
    <FormikProvider value={formik}>
      <Form.Content noValidate autoComplete='off'>
        <Space>
          <Form.TextField
            id='description'
            name='description'
            label={intl.formatMessage(messages.descriptionLabel)}
            placeholder={intl.formatMessage(messages.descriptionPlaceholder)}
            required
            autoFocus
            onKeyDown={handleDescriptionKeyDown}
          />
          <Form.TextField
            id='text'
            name='text'
            label={intl.formatMessage(messages.contentLabel)}
            placeholder={intl.formatMessage(messages.contentPlaceholder)}
            required
            multiline
            minRows={3}
          />

          <Divider />
          <Space direction='horizontal' justify='end'>
            <TertiaryButton type='button' onClick={onCancel}>
              {intl.formatMessage(messages.cancelButton)}
            </TertiaryButton>
            <PrimaryButton type='submit' isLoading={isSaving}>
              {intl.formatMessage(messages.addButton)}
            </PrimaryButton>
          </Space>
        </Space>
      </Form.Content>
    </FormikProvider>
  )
}

CreatePODisclaimerLabelForm.displayName = 'CreatePODisclaimerLabelForm'
